import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Alert } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { OnboardingLayout } from './onBoardingLayOut';
import { useConfirmSignupMutation, useResendConfirmationMutation } from "../../Services/create-account";

export const ActivateAccount: React.FC = () => {
  const navigate = useNavigate();
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmSignup, { isLoading }] = useConfirmSignupMutation();
  const [resendConfirmation, { isLoading: isResending }] = useResendConfirmationMutation();
  const [codeError, setCodeError] = useState<string | undefined>();
  const [apiError, setApiError] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  const email = localStorage.getItem("activationEmail");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setConfirmationCode(val);
    if (codeError) setCodeError(undefined);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setApiError(null);

    if (!email) {
      setApiError("Session expired. Please sign up again.");
      return;
    }

    if (confirmationCode.length !== 6) {
      setCodeError("Please enter the 6-digit code from your email.");
      return;
    }

    try {
      const response = await confirmSignup({ email, confirmationCode }).unwrap();
      localStorage.setItem("idToken", response.idToken);
      localStorage.setItem("accessToken", response.accessToken);
      localStorage.setItem("authProvider", "email");
      navigate('/connect-hubspot');
    } catch (error: any) {
      setApiError(error?.data?.message || error?.message || "Invalid code. Please try again.");
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResendMsg(null);
    setApiError(null);
    try {
      await resendConfirmation({ email }).unwrap();
      setResendMsg("A new code has been sent to your email.");
    } catch (error: any) {
      setApiError(error?.data?.message || error?.message || "Failed to resend code. Please try again.");
    }
  };

  return (
    <OnboardingLayout currentStep={1}>
      <Box>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Activate Your Account
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Enter the 6-digit confirmation code sent to your email.
        </Typography>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>
        )}

        {resendMsg && (
          <Alert severity="success" sx={{ mb: 2 }}>{resendMsg}</Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Confirmation Code"
            value={confirmationCode}
            onChange={handleChange}
            sx={{ mb: 2 }}
            error={!!codeError}
            helperText={codeError}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isLoading}
            sx={{
              py: 1.5,
              textTransform: 'none',
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' }
            }}
          >
            {isLoading ? "Verifying..." : "Continue"}
          </Button>
        </Box>

        <Box textAlign="center" mt={2}>
          <Button
            variant="text"
            size="small"
            disabled={isResending}
            onClick={handleResend}
            sx={{ textTransform: 'none', color: '#2563eb' }}
          >
            {isResending ? "Sending..." : "Resend Verification Code"}
          </Button>
        </Box>
      </Box>
    </OnboardingLayout>
  );
};